/**
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const Vect = require('../types/Vect')
const kernel = require('../kernel/kernel')
const CLignePolygonaleAncetre = require('./CLignePolygonaleAncetre')
module.exports = CPolygone

/**
 * Classe polygone.
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si élément final de construction.
 * @param {mtg32.Color} couleur : La couleur.
 * @param {Boolean} masque : true si l'objet est masqué.
 * @param {mtg32.StyleTrait} style : Le style du tracé.
 * @param {Array} colPoints :  Tableau de CNoeudPointeurSurPoint qui donne les sommets.
 * @returns {CPolygone}
 */
function CPolygone (listeProprietaire, impProto, estElementFinal, couleur, masque, style, colPoints) {
  if (arguments.length === 1) CLignePolygonaleAncetre.call(this, listeProprietaire);
  else CLignePolygonaleAncetre.call(this, listeProprietaire, impProto, estElementFinal, couleur, masque, style, colPoints);
}
CPolygone.prototype = new CLignePolygonaleAncetre();
CPolygone.prototype.constructor = CPolygone;
CPolygone.prototype.superClass = "CLignePolygonaleAncetre";
CPolygone.prototype.className = "CPolygone";

CPolygone.prototype.getClone = function(listeSource, listeCible) {
  var colClone = new Array(this.nombrePoints);
  for (var i = 0; i < this.nombrePoints; i++) {
    var noeud = this.colPoints[i];
    colClone[i] = noeud.getClone(listeSource, listeCible);
  }
  var ind1 = listeSource.indexOf(this.impProto);
  return new CPolygone(listeCible, listeCible.get(ind1, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.masque, this.style.getClone(), colClone);
};
CPolygone.prototype.createg = function() {
  var style = "";
  var stroke = this.style.stroke;
  if (stroke.length !== 0) style += "stroke-dasharray:"+stroke+";";
  var strokewidth = this.style.strokeWidth;
  style += "stroke-width:"+strokewidth+";";
  // style += "stroke:"+this.couleur.rgb()+";"; Modifié version 4.9.9.4
  style += "stroke:"+this.color+";";
  style += "fill:none";
  return kernel.cens("polygon",{
    points : this.getPoints(),
    style : style,
    "pointer-events" : "none"
  });
};
CPolygone.prototype.getPoints = function() {
  var points = "";
  // Pour un polygone, le dernier point est le même que le premier mais pas pour svg
  for (var i = 0; i < this.nombrePoints - 1; i ++) {
    points += kernel.round3dg(this.absPoints[i]) + "," + kernel.round3dg(this.ordPoints[i]) + " ";
  }
  return points;
};
// Version JavaScript : trace est spécifique pour CPolygone et CLigneBrisee
CPolygone.prototype.trace = function(svg) {
  var g = this.createg();
  g.setAttribute("id",this.id);
  svg.appendChild(g);
  this.g = g;
};
CPolygone.prototype.update = function() {
  var g = this.g;
  g.setAttribute("points",this.getPoints());
};
CPolygone.prototype.confonduAvec = function(p) {
  var ptp1,ptp2,i,j,resultat;

  if (p.className === this.className) {
    var nombrePoints = this.nombrePoints;
    if (nombrePoints !== p.nombrePoints) return false;
    // On regarde si un polynôme a déjà été créé avec les mêmes sommets
    // dans le	même ordre. On ne regarde pas le dernier sommet qui
    // est le même que le premier}
    nombrePoints--;
    j = 0;
    do
    {
      ptp1 = this.colPoints[0].pointeurSurPoint;
      ptp2 = p.colPoints[j % nombrePoints].pointeurSurPoint;
      resultat = (ptp1 === ptp2);
      i = 1;
      while (resultat && (i <= nombrePoints-1))
      {
        ptp1 = this.colPoints[i].pointeurSurPoint;
        ptp2 = p.colPoints[(i+j) % nombrePoints].pointeurSurPoint;
        resultat = resultat && (ptp1 === ptp2);
        i++;
      }
      j++;
    }
    while ((j !== nombrePoints) && !resultat);

    if (resultat) return true;
    // On regarde en tournant dans l'autre sens
    j = 0;
    do
    {
      ptp1 = this.colPoints[nombrePoints-1].pointeurSurPoint;
      ptp2 = p.colPoints[j % nombrePoints].pointeurSurPoint;
      resultat = (ptp1 === ptp2);
      i = 1;
      while (resultat && (i <= nombrePoints-1))
      {
        ptp1 = this.colPoints[nombrePoints-1-i].pointeurSurPoint;
        ptp2 = p.colPoints[(i+j) % nombrePoints].pointeurSurPoint;
        resultat = resultat && (ptp1 === ptp2);
        i++;
      }
      j++;
    }
    while ((j !== nombrePoints) && !resultat);
    return resultat;
  }
  else return false;
};

CPolygone.prototype.abscisseMinimale = function() {
  return 0;
};
CPolygone.prototype.abscisseMaximale = function() {
  return 1-1e-12;
};
CPolygone.prototype.getNature = function() {
  return NatObj.NPolygone;
};
CPolygone.prototype.lieALigneFermee = function() {
  return true;
};
/**
 * Fonction renvoyant true si le point de coordonnées (x; y) est intérieur au polygône
 * c'est à dire s'il est sur un des côtés ou si la somme des mesures principales d'angle orienté
 * est un multiple impair de 2*pi.
 */
CPolygone.prototype.pointInterieur = function(x, y) {
  var sommeang = 0;
  var taille = this.colPoints.length;
  for (var i = 0; i < taille - 1; i++) {
    var noeud1 = this.colPoints[i];
    var x1 = noeud1.pointeurSurPoint.x;
    var y1 = noeud1.pointeurSurPoint.y;
    var noeud2 = this.colPoints[i + 1];
    var x2 = noeud2.pointeurSurPoint.x;
    var y2 = noeud2.pointeurSurPoint.y;
    if (kernel.appartientSegment(x, y, x1, y1, x2, y2)) return true;
    var u = new Vect(x, y, x1, y1);
    var v = new Vect(x, y, x2, y2);
    sommeang += u.mesureAngleVecteurs(v);
  }
  var q = Math.floor(sommeang/(2*Math.PI)+0.5);
  // Modifié version 4.8
  // return q != 2*Math.floor(q/2);
  return !kernel.zero13(q - 2*Math.floor(q/2));
};
CPolygone.prototype.determinePositionInterieure = function(x, y, indicePremierSommet, estSurCote, alpha, beta, gamma) {
  var i,nbSommets,nd1,nd2,nd3,nd4,a,b,c,d,e;
  nbSommets = this.nombrePoints - 1;
  for (i = 0; i < nbSommets; i++) {
    nd1 = this.colPoints[i];
    b = nd1.pointeurSurPoint;
    nd2 = this.colPoints[i+1];
    c = nd2.pointeurSurPoint;
    if (kernel.appartientSegment(x, y, b.x, b.y, c.x, c.y)) {
      indicePremierSommet.setValue(i);
      kernel.coefficientsPointSurSegment(x, y, b.x, b.y, c.x, c.y, alpha, beta);
      estSurCote.setValue(true);
      return true;
    }
  }
  var nda = this.colPoints[0];
  a = nda.pointeurSurPoint;
  for (i = 1; i < nbSommets - 1; i++) {
    nd3 = this.colPoints[i];
    d = nd3.pointeurSurPoint;
    nd4 = this.colPoints[i+1];
    e = nd4.pointeurSurPoint;
    if (kernel.estInterieurATriangle(x, y, a.x, a.y, d.x, d.y, e.x, e.y)) {
      indicePremierSommet.setValue(i);
      // En appelant I le point d'intersection on calcule les coordonnées barycentriques du point
      // de coordonnées (x; y) par rapport au triangle ICD
      kernel.coefficientsPointInterieur(x, y, a.x, a.y, d.x, d.y, e.x, e.y, alpha, beta, gamma);
      estSurCote.setValue(false);
      return true;
    }
  }
  return false;
};
CPolygone.prototype.chaineDesignation = function() {
  return "desPolygone";
};

